import React, { useState, useEffect, useRef, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Typography,
  Box,
  Divider,
  Tooltip
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  dialogContent: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  chipSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.7)
  },
  chip: {
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "12px",
    borderRadius: "6px",
    transition: "all 0.15s ease-in-out",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    }
  },
  systemChip: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe"
  },
  flowChip: {
    backgroundColor: "#fef3c7",
    color: "#92400e",
    border: "1px solid #fde68a"
  }
}));

// Variáveis do sistema {{}} - rótulo limpo ("name"), valor inserido {{name}}
const SYSTEM_VARS = [
  "name",
  "firstName",
  "userName",
  "protocol",
  "ms",
  "date",
  "hour",
  "ticket_id",
  "queue",
  "connection"
];

// Variáveis padrões do fluxo/webhook ${}
const DEFAULT_FLOW_VARS = ["nome", "numero", "email"];

const FlowBuilderAddNoteModal = ({ open, onSave, onUpdate, data, close, flowVariables = [] }) => {
  const classes = useStyles();
  const textFieldRef = useRef(null);

  const [activeModal, setActiveModal] = useState(false);
  const [textDig, setTextDig] = useState("");
  const [labels, setLabels] = useState({
    title: "Adicionar anotação interna",
    btn: "Adicionar"
  });

  // Consolidar variáveis do fluxo tipo ${} garantindo apenas nomes limpos únicos
  const mergedFlowVars = useMemo(() => {
    const set = new Set(DEFAULT_FLOW_VARS);

    // Adicionar vindas da prop
    if (Array.isArray(flowVariables)) {
      flowVariables.forEach(v => {
        if (!v) return;
        const cleanName = (typeof v === "string" ? v : (v.name || v.key || ""))
          .replace(/[${}]/g, "")
          .trim();
        if (cleanName) set.add(cleanName);
      });
    }

    // Adicionar também do localStorage se existirem
    try {
      const local = JSON.parse(localStorage.getItem("variables") || "[]");
      if (Array.isArray(local)) {
        local.forEach(v => {
          if (!v) return;
          const cleanName = (typeof v === "string" ? v : (v.name || v.key || ""))
            .replace(/[${}]/g, "")
            .trim();
          if (cleanName) set.add(cleanName);
        });
      }
    } catch (e) {
      console.warn("Erro ao ler variáveis locais:", e);
    }

    return Array.from(set);
  }, [flowVariables]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar anotação interna",
        btn: "Salvar"
      });
      setTextDig(data?.data?.text || data?.data?.label || "");
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar anotação interna ao fluxo",
        btn: "Adicionar"
      });
      setTextDig("");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleInsertVariable = (varText) => {
    const input = textFieldRef.current?.querySelector("textarea");
    if (input) {
      const start = input.selectionStart ?? textDig.length;
      const end = input.selectionEnd ?? textDig.length;
      const newText = textDig.substring(0, start) + varText + textDig.substring(end);
      setTextDig(newText);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + varText.length, start + varText.length);
      }, 50);
    } else {
      setTextDig(prev => prev + varText);
    }
  };

  const handleSave = () => {
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: {
          ...data.data,
          text: textDig,
          label: textDig
        }
      });
    } else if (open === "create") {
      handleClose();
      onSave({
        text: textDig,
        label: textDig
      });
    }
  };

  return (
    <Dialog
      open={activeModal}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle id="note-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <NoteAltIcon style={{ color: "#d97706" }} />
          <span>{labels.title}</span>
        </Box>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <Alert severity="warning">
          <strong>Anotação Exclusiva para Usuários:</strong> Esta anotação será registrada internamente no ticket (como nota privada visível na conversa e no histórico do cliente). Ela <strong>NÃO</strong> é enviada para o WhatsApp do cliente.
        </Alert>

        <TextField
          ref={textFieldRef}
          label="Texto da Anotação Interna"
          placeholder="Digite a anotação interna. Clique nas variáveis abaixo para inseri-las."
          multiline
          rows={6}
          variant="outlined"
          value={textDig}
          onChange={e => setTextDig(e.target.value)}
          fullWidth
        />

        {/* Variáveis do Sistema {{}} */}
        <Box className={classes.chipSection}>
          <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700, letterSpacing: "0.5px" }}>
            VARIÁVEIS DO SISTEMA {`{{}}`} (Clique para inserir):
          </Typography>
          <Box className={classes.chipContainer}>
            {SYSTEM_VARS.map(varName => (
              <Tooltip key={varName} title={`Inserir {{${varName}}}`} arrow>
                <Chip
                  label={varName}
                  size="small"
                  variant="outlined"
                  className={`${classes.chip} ${classes.systemChip}`}
                  onClick={() => handleInsertVariable(`{{${varName}}}`)}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>

        <Divider style={{ margin: "4px 0" }} />

        {/* Variáveis Globais e do Fluxo ${} */}
        <Box className={classes.chipSection}>
          <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700, letterSpacing: "0.5px" }}>
            VARIÁVEIS DO FLUXO E GLOBAIS {`${`\${}`}`} (Clique para inserir):
          </Typography>
          <Box className={classes.chipContainer}>
            {mergedFlowVars.map(varName => (
              <Tooltip key={varName} title={`Inserir \${${varName}}`} arrow>
                <Chip
                  label={varName}
                  size="small"
                  variant="outlined"
                  className={`${classes.chip} ${classes.flowChip}`}
                  onClick={() => handleInsertVariable(`\${${varName}}`)}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary" variant="outlined">
          {i18n.t("contactModal.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          style={{ backgroundColor: "#d97706", color: "#ffffff" }}
        >
          {labels.btn}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddNoteModal;
